"use client";

import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FiArrowLeft } from "react-icons/fi";
import Navbar from "../../../../../components/navbar.jsx";
import Footer from "../../../../../components/footer.jsx";
import Button from "../../../../../components/Button.jsx";
import ConfirmPaymentModal from "../../../components/ConfirmPaymentModal.jsx";
import PaymentCompleteModal from "../../../components/PaymentCompleteModal.jsx";
import PrintReceiptDialog from "../../../../../components/print/views/PrintReceiptDialog";
import styles from "../css/cardPayment.module.css";
import { getPaymentTotal as apiGetPaymentTotal, setPaymentTotal as apiSetPaymentTotal, confirmNonCash as apiConfirmNonCash, completeCodPayment as apiCompleteCodPayment, clearCart as apiClearCart, getSplitState as apiGetSplitState, confirmSplitPayment as apiConfirmSplitPayment, startSplitPayment as apiStartSplitPayment } from "../../../../../api/frontShop/CheckOutAPI.js";
import api from "../../../../../api";
import { recordDebtorsPayment } from "../../../../../api/frontShop/DebtorsAPI.js";

export default function CardPaymentView() {
  const location = useLocation();
  const navigate = useNavigate();

  const [total, setTotal] = useState(0);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showComplete, setShowComplete] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [amountGiven, setAmountGiven] = useState("");
  const [amountPaidUsed, setAmountPaidUsed] = useState(0);
  const [splitOutstanding, setSplitOutstanding] = useState(null);
  const [printOpen, setPrintOpen] = useState(false);
  const [receiptNo, setReceiptNo] = useState("");
  const paymentMethod =
    location.state && typeof location.state.paymentMethod === "string"
      ? location.state.paymentMethod
      : "Card";

  const fromSplit = !!(location.state && location.state.splitFlow);
  const fromDebtorsSplit = !!(location.state && location.state.fromDebtors);
  const method1 =
    location.state && typeof location.state.method1 === "string"
      ? location.state.method1
      : paymentMethod === "Nedbank"
        ? "nedbank"
        : paymentMethod === "Ikhokha"
          ? "ikhokha"
          : "card";
  const fromCOD = !!(location.state && location.state.fromCOD);
  const returnTo = location.state && typeof location.state.returnTo === "string" ? location.state.returnTo : "";

  useEffect(() => {
    let cancelled = false;
    async function loadTotal() {
      try {
        const isDebtors = location.pathname.startsWith("/frontshop/debtors/payment-types/card");
        const isSplitStep2 = location.pathname.startsWith("/frontshop/payment-types/split/step2/method2=");

        // Coming from COD: always respect the passed total (do not overwrite with checkout session total)
        if (fromCOD && location.state && typeof location.state.total === "number") {
          setTotal(location.state.total);
          return;
        }

        if (isSplitStep2) {
          const { data: json } = await apiGetSplitState();
          if (json?.success && json?.data) {
            const outstanding = Number(json.data.outstanding) || 0;
            setSplitOutstanding(outstanding);
            const totalDueFromState = (location.state && typeof location.state.total === "number")
              ? Number(location.state.total)
              : (typeof json.data.total === "number" ? Number(json.data.total) : null);
            if (totalDueFromState != null) {
              setTotal(totalDueFromState);
            } else {
              const { data: tot } = await apiGetPaymentTotal();
              if (!cancelled && tot?.success && typeof tot.total === "number") setTotal(tot.total);
            }
            setAmountGiven(outstanding.toFixed(2));
            return; // only return early on success
          }
          // Fall through if state not available
        }

        if (location.state && typeof location.state.total === "number") {
          setTotal(location.state.total);
          if (isSplitStep2 && splitOutstanding != null) {
            const out = Number(splitOutstanding) || 0;
            setAmountGiven(out.toFixed(2));
          }
        } else if (isDebtors) {
          try {
            const st = sessionStorage.getItem("debtors_total");
            const n = Number(st);
            if (Number.isFinite(n)) setTotal(n);
          } catch { }
        }
        if (!isDebtors) {
          const { data: json } = await apiGetPaymentTotal();
          if (!cancelled && json?.success && typeof json.total === "number") {
            setTotal(json.total);
          }
          await apiSetPaymentTotal();
        }
      } catch { }
    }
    loadTotal();
    return () => {
      cancelled = true;
    };
  }, [location.state, splitOutstanding]);

  const numericGiven = (() => {
    const raw = amountGiven == null ? "" : String(amountGiven);
    const cleaned = raw.replace(/[^0-9.,]/g, "").replace(",", ".");
    const value = parseFloat(cleaned);
    return Number.isFinite(value) ? value : 0;
  })();

  const isSplitMethod1Card =
    fromSplit && ["card", "nedbank", "ikhokha"].includes(method1);
  const isDebtorsDirect = location.pathname.startsWith("/frontshop/debtors/payment-types/card");
  const isSplitMethod2Card =
    location.pathname.startsWith("/frontshop/payment-types/split/step2/method2=card") ||
    location.pathname.startsWith("/frontshop/payment-types/split/step2/method2=nedbank") ||
    location.pathname.startsWith("/frontshop/payment-types/split/step2/method2=ikhokha");

  const balanceOutstanding = isSplitMethod2Card
  ? Math.max(splitOutstanding ?? total, 0)
  : (fromSplit || isDebtorsDirect)
    ? Math.max(total - numericGiven, 0)
    : 0;

  // Clamp Amount Given to Total Due in Debtors flow
  useEffect(() => {
    if (!isDebtorsDirect) return;
    if (!Number.isFinite(numericGiven)) return;
    if (total > 0 && numericGiven > total) {
      setAmountGiven(total.toFixed(2));
    }
    if (numericGiven < 0) {
      setAmountGiven("0.00");
    }
  }, [isDebtorsDirect, numericGiven, total]);

  const isFullyCoveredInSplit =
    isSplitMethod1Card && numericGiven >= total && total > 0;

  const handleBack = () => {
    if (fromSplit) {
      navigate("/frontshop/payment-types/split", {
        state: { total, fromDebtors: fromDebtorsSplit },
      });
      return;
    }
    if (fromCOD) {
      navigate("/frontshop/payment-types/cod/payment", { state: { total } });
      return;
    }
    if (fromDebtorsSplit) {
      navigate("/frontshop/debtors/payment-types", {
        state: { total, fromDebtors: true },
      });
      return;
    }
    navigate("/frontshop/payment-types", { state: { total } });
  };

  const handleMarkPaid = async () => {
  if (isSplitMethod1Card) {
    if (isFullyCoveredInSplit) {
      setShowConfirm(true);
      return;
    }
    try {
      const method1Code = paymentMethod === "Nedbank" ? "NEDB" : (paymentMethod === "Ikhokha" ? "IKHK" : "CARD");
      await apiStartSplitPayment(Number(numericGiven) || 0, method1Code);
    } catch {}
    navigate("/frontshop/payment-types/split/step2", {
      state: { total, method1, fromDebtors: fromDebtorsSplit },
    });
    return;
  }

    if (fromSplit) {
      navigate("/frontshop/payment-types/split/step2", {
        state: { total, method1, fromDebtors: fromDebtorsSplit },
      });
      return;
    }
    setShowConfirm(true);
  };

  const resolveOptionId = () => {
    if (paymentMethod === "Nedbank") return "NEDB";
    if (paymentMethod === "Ikhokha") return "IKHK";
    return "CARD";
  };

  const handleConfirm = async () => {
    setError("");
    setLoading(true);
    try {
      if (fromCOD) {
        const rec = location.state && typeof location.state.recieptno === "string" ? location.state.recieptno : "";
        if (!rec) throw new Error("Missing receipt number for COD payment");
        await apiCompleteCodPayment(rec, resolveOptionId());
        setShowConfirm(false);
        setShowComplete(true);
        setReceiptNo(String(rec));
        setPrintOpen(true);
        return;
      }

      if (isDebtorsDirect) {
        const amountToPay = Math.max(0, Math.min(total, Number(numericGiven) || 0));
        const balance = Math.max(0, Number((total - amountToPay).toFixed(2)));
        let pid = null;
        try {
          const ss = sessionStorage.getItem("debtors_patientid");
          if (ss) pid = Number(ss) || null;
          if (!pid) {
            const raw = localStorage.getItem("frontshop_customer");
            if (raw) pid = JSON.parse(raw)?.patientid || null;
          }
        } catch { }
        if (!pid) throw new Error("No patient selected");
        const { data: json } = await recordDebtorsPayment(pid, {
          amount: amountToPay,
          balance,
          paymentMethod,
          simple: paymentMethod === "EFT",
        });
        setAmountPaidUsed(amountToPay);
        setShowConfirm(false);
        setShowComplete(true);
        setReceiptNo("");
        setPrintOpen(false);
      } else if (isSplitMethod2Card) {
        const method2 = resolveOptionId();
        const { data: json } = await apiConfirmSplitPayment(method2);
        setShowConfirm(false);
        setShowComplete(true);
        const rec = json?.data?.recieptno ? String(json.data.recieptno) : "";
        if (rec) {
          setReceiptNo(rec);
          setPrintOpen(true);
        }
      } else {
        const optionid = resolveOptionId();
        const { data: json } = await apiConfirmNonCash(optionid);
        setShowConfirm(false);
        setShowComplete(true);
        const rec = json?.data?.recieptno ? String(json.data.recieptno) : "";
        if (rec) {
          setReceiptNo(rec);
          setPrintOpen(true);
        }
      }
    } catch (e) {
      setError(e?.message || "Payment failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.wrapper}>
      <Navbar onBack={handleBack} />

      <main className={styles.main}>
        <div className={styles.card}>
          <div className={styles.header}>
            <h1 className={styles.title}>{paymentMethod} Payment</h1>
          </div>

          <section className={styles.totalSection}>
            <div className={styles.totalBox}>
              <div className={styles.totalLabel}>Total Due</div>
              <div className={styles.totalAmount}>R {total.toFixed(2)}</div>
            </div>
          </section>

          {(fromSplit || isDebtorsDirect || isSplitMethod2Card) && (
            <>
              <section className={styles.amountSection}>
                <div className={styles.amountLabel}>Amount Given</div>
                <input
                  type="text"
                  className={styles.amountInput}
                  value={amountGiven}
                  readOnly={isSplitMethod2Card}
                  onChange={(e) => {
                    if (isSplitMethod2Card) return;
                    const raw = e.target.value || "";
                    const cleaned = raw.replace(/[^0-9.,]/g, "");
                    setAmountGiven(cleaned);
                  }}
                  placeholder="0.00"
                />
              </section>

              <section className={styles.balanceSection}>
                <div className={styles.balanceBox}>
                  <div className={styles.balanceLabel}>Balance Outstanding</div>
                  <div className={styles.balanceAmount}>
                    R {balanceOutstanding.toFixed(2)}
                  </div>
                </div>
              </section>
            </>
          )}

          <div className={styles.buttonsRow}>
            <Button
              type="button"
              variant="cancel"
              className={styles.backBtn}
              onClick={handleBack}
            >
              <FiArrowLeft style={{ marginRight: 6 }} />
              Back
            </Button>
            <Button
              type="button"
              variant="approve"
              className={styles.payBtn}
              onClick={handleMarkPaid}
            >
              {isFullyCoveredInSplit
                ? "Mark as paid"
                : isSplitMethod1Card
                  ? "Mark as partially paid"
                  : isDebtorsDirect && total > 0 && numericGiven < total
                    ? "Mark as partially paid"
                    : "Mark as paid"}
            </Button>
          </div>
        </div>
      </main>

      <div className={styles.footerWrapper}>
        <Footer />
      </div>

      {showConfirm && (
        <ConfirmPaymentModal
          total={total}
          amountPaid={isDebtorsDirect ? Math.max(0, Math.min(total, Number(numericGiven) || 0)) : undefined}
          onCancel={() => setShowConfirm(false)}
          onConfirm={handleConfirm}
        />
      )}

      {showComplete && (
        <PaymentCompleteModal
          amountPaid={isDebtorsDirect ? amountPaidUsed : total}
          paymentMethod={paymentMethod}
          discountApplied={0}
          onNewSale={() => {
            if (isDebtorsDirect) {
              setPrintOpen(false);
              setReceiptNo("");
              setShowComplete(false);
              if (returnTo) {
                navigate(returnTo, {
                  state: {
                    ...(location.state || {}),
                    refreshToken: Date.now(),
                  },
                });
              } else {
                navigate("/frontshop/debtors/transactions");
              }
              return;
            }
            apiClearCart()
              .catch(() => { })
              .finally(() => {
                setPrintOpen(false);
                setReceiptNo("");
                setShowComplete(false);
                navigate("/frontshop");
              });
          }}
          onClose={() => {
            setShowComplete(false);
            if (isDebtorsDirect) {
              if (returnTo) {
                navigate(returnTo, {
                  state: {
                    ...(location.state || {}),
                    refreshToken: Date.now(),
                  },
                });
              } else {
                navigate("/frontshop/debtors/transactions");
              }
            }
          }}
        />
      )}

      <PrintReceiptDialog
        open={printOpen}
        recieptno={receiptNo}
        onClose={() => setPrintOpen(false)}
      />
    </div>
  );
}
